import React, { useState, useEffect } from 'react';
import { Download, FileText, User, Search, Calendar, MapPin, Clock, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const AuditTrailReport = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [auditReport, setAuditReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    year: 1990,
    month: 1,
    day: 1,
    hour: 12,
    minute: 0,
    latitude: 28.6139,
    longitude: 77.2090,
    gender: 'male'
  });
  
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [fetchId, setFetchId] = useState(0);
  
  // Fetch users with pagination and search support
  const fetchUsers = async (search = '', page = 1, append = false, thisFetchId = 0) => {
    try {
      if (page === 1 && !append) setLoadingUsers(true);
      
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const url = `${API_URL}/api/audit/users?page=${page}&limit=50${searchParam}`;
      console.log('[AuditTrail] Fetching users from:', url, 'fetchId:', thisFetchId);
      
      const response = await fetch(url);
      
      console.log('[AuditTrail] Response status:', response.status, 'fetchId:', thisFetchId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[AuditTrail] Data received:', { success: data.success, total: data.total_users, usersCount: data.users?.length, search: search, fetchId: thisFetchId });
      
      if (data.success && data.users) {
        console.log('[AuditTrail] Updating users for search:', search, 'fetchId:', thisFetchId);
        if (append) {
          setUsers(prev => [...prev, ...data.users]);
        } else {
          setUsers(data.users);
        }
        setTotalUsers(data.total_users);
        setCurrentPage(page);
        setCurrentSearchTerm(search);
        setHasMore(data.users.length === 50 && (append ? users.length + data.users.length : data.users.length) < data.total_users);
      }
      setLoadingUsers(false);
    } catch (error) {
      console.error('[AuditTrail] Error fetching users:', error, 'fetchId:', thisFetchId);
      setLoadingUsers(false);
    }
  };

  // Initial fetch on mount - only once
  useEffect(() => {
    let mounted = true;
    const initialFetch = async () => {
      if (mounted && searchTerm === '') {
        const id = Date.now();
        setFetchId(id);
        await fetchUsers('', 1, false, id);
      }
    };
    initialFetch();
    return () => { mounted = false; };
  }, []);
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Debounce search - wait 300ms after typing stops
    const timeout = setTimeout(() => {
      console.log('[AuditTrail] Search triggered for:', value);
      const id = Date.now();
      setFetchId(id);
      fetchUsers(value, 1, false, id);
    }, 300);
    
    setSearchTimeout(timeout);
  };
  
  // Load more users when scrolling
  const loadMoreUsers = async () => {
    if (!hasMore || loadingUsers) return;
    const id = Date.now();
    setFetchId(id);
    fetchUsers(currentSearchTerm, currentPage + 1, true, id);
  };

  const generateReport = async (userData) => {
    try {
      setLoading(true);
      setAuditReport(null);
      
      const response = await fetch(`${API_URL}/api/audit/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      if (data.success) {
        setAuditReport(data.report);
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (userData) => {
    try {
      setLoadingPdf(true);
      
      const response = await fetch(`${API_URL}/api/audit/generate/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_trail_${userData.name.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    
    // Parse birth data from user info
    const dateParts = (user.birth_date || '1990-01-01').split('-');
    const timeParts = (user.birth_time || '12:00').split(':');
    
    const userData = {
      name: user.name || user.id,
      year: parseInt(dateParts[0]) || 1990,
      month: parseInt(dateParts[1]) || 1,
      day: parseInt(dateParts[2]) || 1,
      hour: parseInt(timeParts[0]) || 12,
      minute: parseInt(timeParts[1]) || 0,
      latitude: user.latitude || 28.6139,
      longitude: user.longitude || 77.2090,
      gender: user.gender || 'Unknown'
    };
    
    await generateReport(userData);
  };

  const handleNewUserSubmit = async (e) => {
    e.preventDefault();
    setSelectedUser({ name: newUserData.name, isNew: true });
    await generateReport(newUserData);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Users are now filtered server-side, no need for local filtering
  const filteredUsers = users;

  const groupTracesBySection = (traces) => {
    const grouped = {};
    traces.forEach(trace => {
      const section = trace.section || 'Unknown';
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(trace);
    });
    return grouped;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600/20 rounded-xl">
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Audit Trail Report</h1>
              <p className="text-slate-400">Deep calculation traces from source to prediction</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Panel - User Selection */}
          <div className="lg:col-span-1 space-y-4">
            {/* Toggle New User Form */}
            <button
              onClick={() => setShowNewUserForm(!showNewUserForm)}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="new-user-toggle"
            >
              <User className="w-5 h-5" />
              {showNewUserForm ? 'Select Existing User' : 'Enter New Birth Details'}
            </button>

            {/* New User Form */}
            {showNewUserForm && (
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <h3 className="text-white font-medium mb-4">Enter Birth Details</h3>
                <form onSubmit={handleNewUserSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Name"
                    value={newUserData.name}
                    onChange={e => setNewUserData({...newUserData, name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 text-sm"
                    data-testid="new-user-name"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      placeholder="Year"
                      value={newUserData.year}
                      onChange={e => setNewUserData({...newUserData, year: parseInt(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-year"
                    />
                    <input
                      type="number"
                      placeholder="Month"
                      min="1"
                      max="12"
                      value={newUserData.month}
                      onChange={e => setNewUserData({...newUserData, month: parseInt(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-month"
                    />
                    <input
                      type="number"
                      placeholder="Day"
                      min="1"
                      max="31"
                      value={newUserData.day}
                      onChange={e => setNewUserData({...newUserData, day: parseInt(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-day"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Hour"
                      min="0"
                      max="23"
                      value={newUserData.hour}
                      onChange={e => setNewUserData({...newUserData, hour: parseInt(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-hour"
                    />
                    <input
                      type="number"
                      placeholder="Minute"
                      min="0"
                      max="59"
                      value={newUserData.minute}
                      onChange={e => setNewUserData({...newUserData, minute: parseInt(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-minute"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      step="0.0001"
                      placeholder="Latitude"
                      value={newUserData.latitude}
                      onChange={e => setNewUserData({...newUserData, latitude: parseFloat(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-lat"
                    />
                    <input
                      type="number"
                      step="0.0001"
                      placeholder="Longitude"
                      value={newUserData.longitude}
                      onChange={e => setNewUserData({...newUserData, longitude: parseFloat(e.target.value)})}
                      className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      data-testid="new-user-lon"
                    />
                  </div>
                  <select
                    value={newUserData.gender}
                    onChange={e => setNewUserData({...newUserData, gender: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="new-user-gender"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    type="submit"
                    disabled={loading || !newUserData.name}
                    className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                    data-testid="generate-new-report-btn"
                  >
                    {loading ? 'Generating...' : 'Generate Report'}
                  </button>
                </form>
              </div>
            )}

            {/* Existing Users List */}
            {!showNewUserForm && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search users by name..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 text-sm"
                      data-testid="search-users"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {searchTerm ? `Found ${filteredUsers.length} matching "${searchTerm}"` : `Showing ${filteredUsers.length} of ${totalUsers} users`}
                  </p>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto">
                  {loadingUsers ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto" />
                      <p className="text-slate-400 mt-2">Loading users...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((user, idx) => (
                      <button
                        key={user.id || idx}
                        onClick={() => handleUserSelect(user)}
                        className={`w-full p-4 text-left border-b border-slate-700/50 hover:bg-slate-700/50 transition-colors ${
                          selectedUser?.id === user.id ? 'bg-blue-600/20 border-l-4 border-l-blue-500' : ''
                        }`}
                        data-testid={`user-${user.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${user.rating === 'AA' ? 'bg-amber-600/30' : 'bg-slate-700'}`}>
                            <User className={`w-4 h-4 ${user.rating === 'AA' ? 'text-amber-400' : 'text-slate-300'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium truncate">{user.name || user.id}</p>
                              {user.rating === 'AA' && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-400 rounded">AA</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                              <Calendar className="w-3 h-3" />
                              <span>{user.birth_date || 'Unknown'}</span>
                              {user.birth_time && user.birth_time !== 'Unknown' && (
                                <>
                                  <Clock className="w-3 h-3 ml-1" />
                                  <span>{user.birth_time}</span>
                                </>
                              )}
                            </div>
                            {user.place && user.place !== 'Unknown' && (
                              <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                                <MapPin className="w-3 h-3" />
                                <span className="truncate">{user.place}</span>
                              </div>
                            )}
                            {user.source === 'astro-databank' && user.bio && (
                              <p className="text-xs text-slate-500 mt-1 truncate">{user.bio}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Report View */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto" />
                <p className="text-white mt-4 text-lg">Generating Audit Trail...</p>
                <p className="text-slate-400 mt-2">Calculating 1000+ traces</p>
              </div>
            ) : auditReport ? (
              <div className="space-y-4">
                {/* Report Header */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-white">{auditReport.meta?.user?.name || 'Unknown'}</h2>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {auditReport.meta?.user?.birth_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {auditReport.meta?.user?.birth_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {auditReport.meta?.user?.latitude?.toFixed(4)}°, {auditReport.meta?.user?.longitude?.toFixed(4)}°
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadPdf(selectedUser?.isNew ? newUserData : {
                        name: selectedUser?.name,
                        year: parseInt((selectedUser?.birth_date || '1990-01-01').split('-')[0]),
                        month: parseInt((selectedUser?.birth_date || '1990-01-01').split('-')[1]),
                        day: parseInt((selectedUser?.birth_date || '1990-01-01').split('-')[2]),
                        hour: parseInt((selectedUser?.birth_time || '12:00').split(':')[0]),
                        minute: parseInt((selectedUser?.birth_time || '12:00').split(':')[1]),
                        latitude: selectedUser?.latitude || 28.6139,
                        longitude: selectedUser?.longitude || 77.2090,
                        gender: selectedUser?.gender || 'Unknown'
                      })}
                      disabled={loadingPdf}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      data-testid="download-pdf-btn"
                    >
                      {loadingPdf ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5" />
                      )}
                      Download PDF
                    </button>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-blue-400">{auditReport.summary?.total_traces || 0}</p>
                      <p className="text-slate-400 text-sm">Total Traces</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-green-400">{Object.keys(auditReport.summary?.section_breakdown || {}).length}</p>
                      <p className="text-slate-400 text-sm">Sections</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-purple-400">8</p>
                      <p className="text-slate-400 text-sm">Domains</p>
                    </div>
                  </div>
                </div>

                {/* Traces by Section */}
                <div className="space-y-2">
                  {Object.entries(groupTracesBySection(auditReport.traces || [])).map(([section, traces]) => (
                    <div key={section} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                      <button
                        onClick={() => toggleSection(section)}
                        className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                        data-testid={`section-${section}`}
                      >
                        <div className="flex items-center gap-3">
                          {expandedSections[section] ? (
                            <ChevronDown className="w-5 h-5 text-blue-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="text-white font-medium">{section.replace(/_/g, ' ').replace(/^\d+\s*/, '')}</span>
                        </div>
                        <span className="text-sm text-slate-400">{traces.length} traces</span>
                      </button>
                      
                      {expandedSections[section] && (
                        <div className="border-t border-slate-700 p-4 space-y-4 max-h-[600px] overflow-y-auto">
                          {traces.map((trace, idx) => (
                            <div key={idx} className="bg-slate-700/30 rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="text-white font-medium">{trace.calculation}</p>
                                  <p className="text-xs text-slate-400 mt-1">{trace.subsection?.replace(/_/g, ' ')}</p>
                                </div>
                                <span className="text-xs text-slate-500">#{trace.id}</span>
                              </div>
                              
                              {trace.formula && (
                                <p className="text-sm text-slate-400 mt-2 font-mono bg-slate-800 p-2 rounded">
                                  {trace.formula}
                                </p>
                              )}
                              
                              {trace.inputs && Object.keys(trace.inputs).length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Inputs:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(trace.inputs).slice(0, 5).map(([key, value]) => (
                                      <span key={key} className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-300">
                                        {key}: {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) + '...' : String(value).slice(0, 30)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {trace.result && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Result:</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(trace.result).slice(0, 6).map(([key, value]) => (
                                      <div key={key} className="text-xs">
                                        <span className="text-green-400">{key}:</span>{' '}
                                        <span className="text-white">
                                          {typeof value === 'object' 
                                            ? (Array.isArray(value) ? `[${value.length} items]` : '{...}')
                                            : typeof value === 'number' 
                                              ? value.toFixed ? value.toFixed(4) : value
                                              : String(value).slice(0, 50)
                                          }
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {trace.interpretation && (
                                <p className="text-sm text-amber-400/80 mt-2 italic">
                                  {trace.interpretation}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto" />
                <p className="text-white mt-4 text-lg">Select a user or enter birth details</p>
                <p className="text-slate-400 mt-2">Generate comprehensive audit trail with 1000+ calculation traces</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailReport;
